import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import { styled, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import DateTimePicker from 'react-datetime-picker';
import React, { FC, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons/faCalendar';
import { Styling } from '@/styling';

const commonDateTimePickerStyle = css`
  & .react-datetime-picker {
    width: 100%;
    height: 53px;
    border-radius: ${Styling.radii('input')};
    ${Styling.typography('input')}

    & .react-datetime-picker__wrapper {
      padding: ${Styling.spacing(1.5, 2)};
      border: 0;
    }

    &--disabled .react-datetime-picker__inputGroup {
      cursor: not-allowed !important;
    }

    & .react-datetime-picker__inputGroup {
      cursor: pointer;
    }

    & .react-datetime-picker__button {
      margin-left: ${Styling.spacing(1.5)};
      color: ${({ theme }) => theme.palette.primary.main};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }

    & .react-calendar {
      padding: ${Styling.spacing(1.5, 2)};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
      border-radius: ${Styling.radii('input')};
      box-shadow: ${Styling.shadow('dropdown')};

      & .react-calendar__tile--now {
        background-color: initial;
      }

      & .react-calendar__tile--active {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }

      & .react-calendar__tile--hasActive {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  }
`;

const RoundDateTimePickerBase = styled.div`
  & .react-datetime-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
    border: 0;

    &:hover {
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) =>
        `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: rgba(255, 255, 255, 0.5);
    }

    & .react-datetime-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDateTimePickerStyle}
`;

const FilledDateTimePickerBase = styled.div`
  & .react-datetime-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
    border: 1px solid #e9e9e9;
    transition:
      100ms background-color,
      100ms border;

    &:hover {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) =>
        `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light4}`};
    }

    & .react-datetime-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDateTimePickerStyle}
`;

export type DateTimeInputProps = {
  className?: string;
  name?: string;
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange?(value: Date): void;
  disabled?: boolean;
  icon?: ReactNode;
};

const DateTimeInput: FC<DateTimeInputProps> = ({
  name,
  value,
  minDate,
  maxDate,
  onChange,
  disabled = false,
}) => {
  const { i18n } = useTranslation();

  return (
    <DateTimePicker
      name={name}
      format="y-MM-dd HH:mm"
      locale={i18n.language}
      disabled={disabled}
      value={value}
      onChange={(date) => onChange?.(date ?? new Date())}
      minDate={minDate}
      maxDate={maxDate}
      calendarIcon={<FontAwesomeIcon icon={faCalendar} size="lg" />}
      clearIcon={null}
    />
  );
};

export const RoundDateTimeInput: FC<DateTimeInputProps> = ({
  className,
  ...props
}) => {
  return (
    <RoundDateTimePickerBase className={className}>
      <DateTimeInput {...props} />
    </RoundDateTimePickerBase>
  );
};

export const FilledDateTimeInput: FC<DateTimeInputProps> = ({
  className,
  ...props
}) => {
  return (
    <FilledDateTimePickerBase className={className}>
      <DateTimeInput {...props} />
    </FilledDateTimePickerBase>
  );
};
